/**
 * People > Learning — portada de la sección (todos los clappers).
 * Los programas son DATOS editables (LEARNING_PROGRAMS en ConfigTable):
 * people/admin ven el botón "+" y el lapicito en cada card, inline — sin
 * pasar por el tab Contenido (ese queda para la estructura del juego).
 * Tabs por ?tab=: game (portada) siempre; dashboard/editor gateados por probe.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, GraduationCap, Joystick, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import {
  STATUS_LABELS_ES,
  deriveStatus,
  pickLang,
  type ArcadeLang,
  type LearningProgram,
} from '@/../../shared/people-learning';
import { LearningDashboard } from './dashboard/LearningDashboard';
import { LearningGovernanceTab } from './LearningGovernanceTab';
import { ContentEditorTab } from './ContentEditorTab';
import { LearningImageField } from './LearningImageField';

export function LearningPage() {
  const { t, i18n } = useTranslation();
  const lang = ((['es', 'en', 'pt'].includes(i18n.language) ? i18n.language : 'es') as ArcadeLang);
  const [searchParams, setSearchParams] = useSearchParams();
  const access = trpc.peopleLearning.getMyAccess.useQuery(undefined, { retry: false, staleTime: 5 * 60_000 });
  const my = trpc.peopleLearning.getMyArcade.useQuery(undefined, { staleTime: 60_000 });
  const programsQ = trpc.peopleLearning.getPrograms.useQuery(undefined, { staleTime: 60_000 });
  const utils = trpc.useUtils();
  const save = trpc.peopleLearning.savePrograms.useMutation({
    onSuccess: () => { utils.peopleLearning.getPrograms.invalidate(); toast.success(t('people-learning.learning.programas_actualizados')); },
    onError: (e) => toast.error(e.message),
  });

  const rawTab = searchParams.get('tab');
  const canDashboard = access.data?.canViewDashboard ?? false;
  const canEdit = access.data?.canEdit ?? false;
  const canGovernance = access.data?.canAdminGovernance ?? false;
  const tab =
    rawTab === 'dashboard' && canDashboard ? 'dashboard'
      : rawTab === 'editor' && canEdit ? 'editor'
        : rawTab === 'permisos' && canGovernance ? 'permisos'
          : 'game';
  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [editing, setEditing] = useState<LearningProgram | 'new' | null>(null);
  const programs = programsQ.data?.programs ?? [];
  // Portadas con imagen (§5): keys → URLs firmadas 1h.
  const imageKeys = programs.map((p) => p.imageKey).filter((k): k is string => !!k);
  const imageUrls = trpc.peopleLearning.getImageUrls.useQuery(
    { keys: imageKeys },
    { enabled: imageKeys.length > 0, staleTime: 55 * 60_000 }
  );
  const status = deriveStatus(my.data?.progress ?? null);

  const persist = (next: LearningProgram[]) => save.mutate({ programs: { programs: next } });
  const removeProgram = (p: LearningProgram) => {
    if (!window.confirm(`¿Quitar "${p.title.es}" de la portada?`)) return;
    persist(programs.filter((x) => x.id !== p.id));
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-100 text-purple-600 dark:bg-purple-900/30">
          <GraduationCap className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight">Learning</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('people_learning.subtitle', 'Los programas de aprendizaje de People, empezando por tu onboarding.')}
          </p>
        </div>
        {canEdit && tab === 'game' && (
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="mr-1 h-4 w-4" /> {t('people-learning.learning.nuevo_programa')}
          </Button>
        )}
      </div>

      {(canDashboard || canEdit || canGovernance) && (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="game">{t('people_learning.tab_game', 'Programas')}</TabsTrigger>
            {canDashboard && <TabsTrigger value="dashboard">{t('people_learning.tab_dashboard', 'Dashboard')}</TabsTrigger>}
            {canEdit && <TabsTrigger value="editor">{t('people_learning.tab_editor', 'Contenido')}</TabsTrigger>}
            {canGovernance && <TabsTrigger value="permisos">{t('people_learning.tab_governance', 'Permisos')}</TabsTrigger>}
          </TabsList>
        </Tabs>
      )}

      {tab === 'game' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {programs.map((p) => (
            <Card key={p.id} className="group relative flex flex-col overflow-hidden">
              {canEdit && (
                <div className="absolute right-2 top-2 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button
                    size="icon" variant="secondary" className="h-7 w-7"
                    onClick={() => setEditing(p)} title="Editar"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {p.kind !== 'arcade' && (
                    <Button
                      size="icon" variant="secondary" className="h-7 w-7 text-destructive"
                      onClick={() => removeProgram(p)} title="Quitar"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              )}
              {p.imageKey && imageUrls.data?.urls[p.imageKey] ? (
                <div className="aspect-16/7 w-full overflow-hidden bg-black">
                  <img src={imageUrls.data.urls[p.imageKey]} alt="" className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="flex aspect-16/7 items-center justify-center bg-black text-5xl">{p.emoji || '🎓'}</div>
              )}
              <div className="flex flex-1 flex-col gap-3 p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold">{pickLang(p.title, lang)}</p>
                  {p.kind === 'arcade' && (
                    <Badge variant={status === 'completado' ? 'default' : 'secondary'}>
                      {STATUS_LABELS_ES[status]}
                    </Badge>
                  )}
                </div>
                {p.desc && <p className="text-sm text-muted-foreground">{pickLang(p.desc, lang)}</p>}
                {p.kind === 'arcade' && my.data?.progress && my.data.progress.points > 0 && (
                  <p className="text-xs text-muted-foreground">{my.data.progress.points} {t('people-learning.learning.clapper_points')}</p>
                )}
                <div className="mt-auto">
                  {p.kind === 'arcade' ? (
                    <Button asChild className="w-full">
                      <Link to="/people/learning/arcade">
                        <Joystick className="mr-2 h-4 w-4" />
                        {status === 'en_progreso'
                          ? t('people_learning.resume', 'Continuar')
                          : status === 'completado'
                            ? t('people_learning.replay', 'Volver a jugar')
                            : t('people_learning.play', 'Jugar')}
                      </Link>
                    </Button>
                  ) : (
                    <Button asChild variant="outline" className="w-full" disabled={!p.url}>
                      <a href={p.url || '#'} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="mr-2 h-4 w-4" />
                        {t('people_learning.open', 'Abrir')}
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'dashboard' && canDashboard && (
        <LearningDashboard scope={access.data?.scope === 'all' ? 'all' : 'team'} hasTeam={access.data?.hasTeam ?? false} canEdit={canEdit} />
      )}
      {tab === 'editor' && canEdit && <ContentEditorTab />}
      {tab === 'permisos' && canGovernance && <LearningGovernanceTab />}

      {editing && (
        <ProgramDialog
          program={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            const list =
              editing === 'new'
                ? [...programs, next]
                : programs.map((x) => (x.id === next.id ? next : x));
            persist(list);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ProgramDialog({ program, onClose, onSave }: {
  program: LearningProgram | null;
  onClose: () => void;
  onSave: (p: LearningProgram) => void;
}) {
  const { t } = useTranslation();
  const isNew = !program;
  const [emoji, setEmoji] = useState(program?.emoji ?? '🎓');
  const [imageKey, setImageKey] = useState<string | undefined>(program?.imageKey);
  const [titleEs, setTitleEs] = useState(program?.title.es ?? '');
  const [titleEn, setTitleEn] = useState(program?.title.en ?? '');
  const [titlePt, setTitlePt] = useState(program?.title.pt ?? '');
  const [descEs, setDescEs] = useState(program?.desc?.es ?? '');
  const [url, setUrl] = useState(program?.url ?? '');
  const isArcade = program?.kind === 'arcade';

  useEffect(() => {
    // noop — estado inicial ya seteado por props
  }, []);

  const canSave = titleEs.trim().length > 0 && (isArcade || url.trim().length > 0);
  const submit = () => {
    if (!canSave) return;
    onSave({
      id: program?.id ?? `prog_${Date.now().toString(36)}`,
      kind: program?.kind ?? 'link',
      emoji: emoji.trim() || undefined,
      imageKey,
      title: { es: titleEs.trim(), en: titleEn.trim() || undefined, pt: titlePt.trim() || undefined },
      desc: descEs.trim() ? { es: descEs.trim() } : undefined,
      url: isArcade ? undefined : url.trim(),
    });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isNew ? t('people-learning.learning.nuevo_programa') : `Editar: ${program?.title.es}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <LearningImageField
            folder="programs"
            imageKey={imageKey}
            onChange={setImageKey}
            label={t('people-learning.learning.imagen_de_portada', 'Imagen de portada (reemplaza al emoji)')}
          />
          <div className="grid grid-cols-[80px_1fr] gap-3">
            <div className="space-y-1.5">
              <Label>Emoji</Label>
              <Input value={emoji} onChange={(e) => setEmoji(e.target.value)} className="h-9 text-center text-lg" maxLength={8} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('people-learning.learning.titulo_es')}</Label>
              <Input value={titleEs} onChange={(e) => setTitleEs(e.target.value)} className="h-9" autoFocus maxLength={120} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('people-learning.learning.titulo_en_opcional')}</Label>
              <Input value={titleEn} onChange={(e) => setTitleEn(e.target.value)} className="h-8 text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('people-learning.learning.titulo_pt_opcional')}</Label>
              <Input value={titlePt} onChange={(e) => setTitlePt(e.target.value)} className="h-8 text-sm" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('people-learning.learning.descripcion')}</Label>
            <Textarea value={descEs} onChange={(e) => setDescEs(e.target.value)} rows={2} maxLength={300} />
          </div>
          {!isArcade && (
            <div className="space-y-1.5">
              <Label>{t('people-learning.learning.link_se_abre_en_pestana_nueva')}</Label>
              <Input
                value={url} onChange={(e) => setUrl(e.target.value)} className="h-9"
                placeholder="https://drive.google.com/… o cualquier recurso"
              />
            </div>
          )}
          {isArcade && (
            <p className="text-xs text-muted-foreground">
              {t('people-learning.learning.este_es_el_curso_gamificado_su')}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={!canSave}>{isNew ? 'Agregar' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
